import { useCallback } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { changePpi } from '../../../../utils/services/complexDocumentsAPI';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../../context/complexVersionContext';
import { useArtboardContext, TEMPLATE_TYPE } from '../../../../context/complexArtboardContext';
import { deepCopy } from '../../../../utils/javascript';
import { COMPLEX_DOCUMENT_VERSION } from '../../../../utils/complexDocumentConstants';
import { DESIGN_AREA_SETTINGS_TAB } from '../../../SidebarMenu/translationValues';

const NAMESPACES = ['DESIGN_AREA_SETTINGS_TAB'];
const blockAndFlowTypes = [TEMPLATE_TYPE.BLOCK, TEMPLATE_TYPE.FLOW];

export const usePpiSettings = (CurrentDesign) => {
  const { t } = useTranslation(NAMESPACES);
  
  const customSelector = useCallback(
    store => {
      const templateId = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID];
      let ppi = store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]?.ppi;
      if (CurrentDesign === TEMPLATE_TYPE.FLOW) {
        const flow = store[store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
        ppi = flow?.ppi;
      } else if (CurrentDesign === TEMPLATE_TYPE.BLOCK) {
        const flow = store[store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
        const pageId = flow?.pages?.[0];
        const blockUUIDs = flow[pageId];
        if (blockUUIDs?.length) {
          for (let i = 0; i < blockUUIDs.length; i++) {
            const block = store[blockUUIDs[i]];
            if (block?.style?.ppi) {
              ppi = block.style.ppi;
              break;
            }
          }
        }
      }
      return {
        resourceId: templateId,
        ppi: ppi,
      };
    },
    [CurrentDesign],
  );
  const [{ resourceId, ppi }, store] = useDocumentContext(customSelector, 'usePpiSettings');

  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, 'usePpiSettings');

  const onPpiChange = useCallback(async value => {
    const payload = {
      ppi: value,
      resourceId: resourceId,
      resourceType: CurrentDesign,
    };
    if (blockAndFlowTypes.includes(CurrentDesign)) {
      payload.isStandalone = true;
    }
    try {
      const response = await changePpi(payload);
      if (response?.statusCode === 1930) {
        const currentStore = store.get();
        if (CurrentDesign === TEMPLATE_TYPE.DOCUMENT) {
          const style = deepCopy(currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]);
          style.ppi = value;
          store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES]: style });
        } else if (CurrentDesign === TEMPLATE_TYPE.FLOW) {
          const flowUUID = currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0];
          const flow = { ...deepCopy(currentStore[flowUUID]), ppi: value };
          store.set({ [flowUUID]: flow });
        } else if (CurrentDesign === TEMPLATE_TYPE.BLOCK) {
          const flow = currentStore[currentStore[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS][0]];
          const pageId = flow?.pages?.[0];
          const blockUUIDs = flow[pageId];
          const storeObj = {};
          blockUUIDs.forEach(blockUUID => {
            const block = deepCopy(currentStore[blockUUID]);
            if (block?.style) {
              block.style.ppi = value;
            }
            storeObj[blockUUID] = block;
          });
          store.set(storeObj);
        }
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: {
            ...lifeCycle,
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date(),
          },
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      message.error(t(DESIGN_AREA_SETTINGS_TAB.PPI_UPDATE_ERROR.key, DESIGN_AREA_SETTINGS_TAB.PPI_UPDATE_ERROR.value));
    }
  }, [CurrentDesign, resourceId, store, lifeCycle, updateVersionStore, t]);

  return {
    ppi: ppi || 300,
    onPpiChange,
  };
};


